import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { isAdminViewMode } from "../adminViewMode.js";

const BLUE = "#2f6fed";
const RED = "#d32f2f";

function money(v) {
  const n = Number(v || 0);
  return n.toLocaleString("ru-RU") + " ₽";
}

function PendingOrderDialog({ order, adminView, onResult }) {
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e) => { if (e.key === "Escape") onResult("close"); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onResult]);

  const items = order?.order_items || [];
  const itemsSum = items.reduce((s, it) => s + Number(it.price || 0) * (it.quantity || 1), 0);
  const canPay = order?.can_pay !== false && !!order?.payment_url;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onResult("close"); }}
      style={{
        position: "fixed", inset: 0, zIndex: 100000,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
        opacity: shown ? 1 : 0,
        transition: "opacity 0.18s ease",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: "100%", maxWidth: 460,
          background: "#fff", borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          padding: 24,
          transform: shown ? "translateY(0) scale(1)" : "translateY(8px) scale(0.98)",
          transition: "transform 0.2s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, color: "#1a1a1a", marginBottom: 8 }}>
          {adminView ? "Незавершённый заказ клиента" : "У вас есть незавершённый заказ"}
        </div>

        <div style={{ fontSize: 15, lineHeight: 1.5, color: "#444", marginBottom: 16 }}>
          {adminView
            ? `Заказ №${order?.order_number} создан, но ещё не оплачен клиентом. Вы открыли книгу в режиме администратора — действия с заказом недоступны (оплату/отмену решает клиент).`
            : `Заказ №${order?.order_number} создан, но ещё не оплачен. Вы можете продолжить оплату или отменить заказ — книги при отмене вернутся в корзину и останутся доступны для редактирования.`}
        </div>

        {items.length > 0 && (
          <div style={{
            border: "1px solid #eee", borderRadius: 12, padding: 12, marginBottom: 16,
            maxHeight: 160, overflowY: "auto",
          }}>
            {items.map((it, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 14, color: "#333", padding: "4px 0",
              }}>
                <span>{it.book_title || it.title || "Фотокнига"}{it.quantity > 1 ? ` × ${it.quantity}` : ""}</span>
                <span style={{ color: "#666" }}>{money((it.price || 0) * (it.quantity || 1))}</span>
              </div>
            ))}
          </div>
        )}

        {/* Разбивка суммы: позиции + доставка − скидка = итог */}
        <div style={{ marginBottom: 16, borderTop: "1px solid #eee", paddingTop: 12 }}>
          {items.length > 0 && (
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: 14, color: "#555", padding: "3px 0",
            }}>
              <span>Книги</span>
              <span>{money(itemsSum)}</span>
            </div>
          )}

          {Number(order?.discount_amount) > 0 && (
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: 14, color: "#2e7d32", padding: "3px 0",
            }}>
              <span>Скидка{order?.discount_percent ? ` ${order.discount_percent}%` : ""}</span>
              <span>−{money(order.discount_amount)}</span>
            </div>
          )}

          <div style={{
            display: "flex", justifyContent: "space-between",
            fontSize: 14, color: "#555", padding: "3px 0",
          }}>
            <span>Доставка</span>
            <span>
              {Number(order?.delivery_price) > 0 ? money(order.delivery_price) : "Бесплатно"}
            </span>
          </div>
        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 22,
        }}>
          <span>Итого</span>
          <span>{money(order?.total_price)}</span>
        </div>

        {adminView ? (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => onResult("close")}
              style={{
                padding: "10px 26px", borderRadius: 12, border: "none",
                background: BLUE, color: "#fff", fontSize: 15, fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Понятно
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between" }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => { setBusy(true); onResult("cancel"); }}
              style={{
                padding: "10px 18px", borderRadius: 12, border: "none",
                background: "#f6e7e7", color: RED, fontSize: 15, fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer", flex: "0 0 auto",
              }}
            >
              Отменить заказ
            </button>
            <button
              type="button"
              disabled={busy || !canPay}
              onClick={() => { setBusy(true); onResult("pay"); }}
              style={{
                padding: "10px 26px", borderRadius: 12, border: "none",
                background: canPay ? BLUE : "#b9ccf5",
                color: "#fff", fontSize: 15, fontWeight: 700,
                cursor: (busy || !canPay) ? "not-allowed" : "pointer",
                flex: "1 1 auto",
              }}
            >
              Оплатить
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function pendingOrderModal(order) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    let resolved = false;
    const finish = (result) => {
      if (resolved) return;
      resolved = true;
      root.unmount();
      host.remove();
      resolve(result);
    };
    const adminView = isAdminViewMode();
    root.render(<PendingOrderDialog order={order} adminView={adminView} onResult={finish} />);
  });
}